import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/atoms";
import { Select } from "@/components/atoms/Select";
import { ChevronDownIcon } from "@/components/icons";
import { ColorInput } from "@/components/molecules/ManualWidgetForm";
import { Settings } from "lucide-react";
import { Switch } from "@/components/molecules/Switch";
import { Divider } from "@/components/atoms";
import { Metric } from "@/modules/product/types";
import { useAuth } from "@/modules/auth";
import { canPublishGlobally, canPublishInsights } from "@/modules/widgets/utils/permissions";
import type { WidgetTypeOption } from "@/components/molecules/ManualWidgetForm";

interface AdvancedSettingsProps {
    isAdvancedOpen: boolean;
    setIsAdvancedOpen: React.Dispatch<React.SetStateAction<boolean>>;
    publishToAll: boolean;
    setPublishToAll: React.Dispatch<React.SetStateAction<boolean>>;
    showAxisLegends: boolean;
    setShowAxisLegends: React.Dispatch<React.SetStateAction<boolean>>;
    widgetColors: string[];
    setWidgetColors: (widgetColors: string[]) => void;
    metrics: Metric[];

    /** True when the selected segment is the campaign segment (segment name === "campaign"). */
    isCampaignSegmentSelected?: boolean;
    /** Display label for the campaign segment, used when no dimension metric is selected. */
    campaignSegmentLabel?: string;
    /** True when the selected level is the campaign breakdown level (and not grouped by a sub-segment). */
    isCampaignLevelSelected?: boolean;

    setFilterByProduct: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterByDimension: React.Dispatch<React.SetStateAction<boolean>>;

    filterByProduct: boolean;
    filterByDimension: boolean;

    widgetType?: WidgetTypeOption | null;
    tableSummaryRow?: "none" | "sum" | "average";
    setTableSummaryRow?: React.Dispatch<React.SetStateAction<"none" | "sum" | "average">>;
    showMetricSelector?: boolean;
    setShowMetricSelector?: React.Dispatch<React.SetStateAction<boolean>>;

    // AI Insights
    enableAIInsights?: boolean;
    setEnableAIInsights?: React.Dispatch<React.SetStateAction<boolean>>;
    publishInsights?: boolean;
    setPublishInsights?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AdvancedSettings = ({
    isAdvancedOpen,
    setIsAdvancedOpen,
    publishToAll,
    setPublishToAll,
    showAxisLegends,
    setShowAxisLegends,
    widgetColors,
    setWidgetColors,
    metrics,
    isCampaignSegmentSelected,
    campaignSegmentLabel,
    isCampaignLevelSelected,
    setFilterByProduct,
    setFilterByDimension,
    filterByProduct,
    filterByDimension,
    widgetType,
    tableSummaryRow,
    setTableSummaryRow,
    showMetricSelector,
    setShowMetricSelector,
    enableAIInsights,
    setEnableAIInsights,
    publishInsights,
    setPublishInsights,
}: AdvancedSettingsProps) => {
    const [filters, setFilters] = useState([]);
    const { user } = useAuth();
    const canPublish = canPublishGlobally(user);
    const canPublishAIInsights = canPublishInsights(user);

    const dimensionForFilter = useMemo(() => {
        const dimensionMetric = metrics?.find((metric) => metric?.type === "dimension");
        return dimensionMetric?.showName;
    }, [metrics]);

    // The campaign filter is available when any dimension metric is selected, OR when the
    // selected segment is the campaign segment, OR when the campaign level is the breakdown.
    const showCampaignFilter = Boolean(dimensionForFilter || isCampaignSegmentSelected || isCampaignLevelSelected);
    const campaignFilterLabel = dimensionForFilter ?? campaignSegmentLabel ?? "Campaign";

    useEffect(() => {
        setFilters([]);
    }, []);

    return (
        <div className="border h-full p-4 rounded border-muted">
            <button type="button" className="flex items-center gap-2 w-full cursor-pointer" onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}>
                <Settings className="w-5 h-5" />
                <span className="font-roboto">Advanced Settings</span>
                <ChevronDownIcon className="w-5 h-5 ml-auto" />
            </button>

            <div className={isAdvancedOpen ? "overflow-y-visible space-y-3 mt-4" : "max-h-0 overflow-y-hidden"}>
                {canPublish && (
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Visible to Everybody</h4>
                            <p className="text-[12px] text-[#5a717a] font-roboto">
                                {publishToAll ? "Anybody with access to the dashboard will see" : "Visible to admins only"}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPublishToAll((prev) => !prev)}
                            className={`inline-flex h-6 w-12 items-center rounded-full transition-colors ${publishToAll ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"}`}
                            aria-pressed={publishToAll}
                        >
                            <span
                                className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${publishToAll ? "translate-x-6" : "translate-x-0"
                                    }`}
                            />
                        </button>
                    </div>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Show Charts Axis legends</h4>
                        <p className="text-[12px] text-[#5a717a] font-roboto">Show the data title on each axis</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowAxisLegends((prev) => !prev)}
                        className={`inline-flex h-6 w-12 items-center rounded-full transition-colors ${showAxisLegends ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"}`}
                        aria-pressed={showAxisLegends}
                    >
                        <span
                            className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${showAxisLegends ? "translate-x-6" : "translate-x-0"
                                }`}
                        />
                    </button>
                </div>

                {widgetType !== "table" && widgetType !== "stacked-bar-line" && (
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Show Metric Selector</h4>
                            <p className="text-[12px] text-[#5a717a] font-roboto">Allow users to toggle metric visibility in the widget header</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowMetricSelector?.((prev) => !prev)}
                            className={`inline-flex h-6 w-12 items-center rounded-full transition-colors ${showMetricSelector ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"}`}
                            aria-pressed={showMetricSelector}
                        >
                            <span
                                className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${showMetricSelector ? "translate-x-6" : "translate-x-0"
                                    }`}
                            />
                        </button>
                    </div>
                )}

                {/* AI Insights Section */}
                <Divider className="my-4" />
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Enable AI Insights</h4>
                        <p className="text-[12px] text-[#5a717a] font-roboto">Widget will have a Generate AI Insights action</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setEnableAIInsights?.((prev) => {
                                if (prev) {
                                    // If disabling AI insights, also disable publish insights
                                    setPublishInsights?.(false);
                                }
                                return !prev;
                            });
                        }}
                        className={`inline-flex h-6 w-12 items-center rounded-full transition-colors ${enableAIInsights ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"}`}
                        aria-pressed={enableAIInsights}
                    >
                        <span
                            className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${enableAIInsights ? "translate-x-6" : "translate-x-0"}`}
                        />
                    </button>
                </div>

                {canPublishAIInsights && (
                    <div className={`flex items-center justify-between ${!enableAIInsights ? 'opacity-50' : ''}`}>
                        <div>
                            <h4 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Publish Insights</h4>
                            <p className="text-[12px] text-[#5a717a] font-roboto">External users will be able to see insights</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPublishInsights?.((prev) => !prev)}
                            disabled={!enableAIInsights}
                            className={`inline-flex h-6 w-12 items-center rounded-full transition-colors ${publishInsights && enableAIInsights ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"} ${!enableAIInsights ? 'cursor-not-allowed' : ''}`}
                            aria-pressed={publishInsights}
                        >
                            <span
                                className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${publishInsights && enableAIInsights ? "translate-x-6" : "translate-x-0"}`}
                            />
                        </button>
                    </div>
                )}

                {widgetType === "table" && tableSummaryRow && setTableSummaryRow ? (
                    <>
                        <Divider className="my-4" />
                        <Select
                            label="Summary row"
                            description="Choose whether to include a Total/Average summary row at the bottom of the table."
                            options={[
                                { id: "none", label: "None" },
                                { id: "sum", label: "Total" },
                                { id: "average", label: "Average" },
                            ]}
                            value={tableSummaryRow}
                            onChange={(value) => setTableSummaryRow(value as any)}
                            clearable={false}
                        />
                    </>
                ) : null}
                <Divider className="my-4" />
                {/** Filters SECTION */}
                <div className="pt-2 space-y-3 pb-2">
                    <Switch checked={filterByProduct} onChange={() => setFilterByProduct((prev) => !prev)} label="Filter by Product" description="Add the filter by product" />
                    {
                        showCampaignFilter && (
                            <Switch checked={filterByDimension} onChange={() => setFilterByDimension((prev) => !prev)} label="Filter by Campaign" description={`Add the filter by ${campaignFilterLabel}`} />
                        )
                    }
                </div>
                <Divider className="my-2" />
                {/** COLORS SECTION */}
                <div className="pt-4">
                    <div className="flex items-center justify-between mb-3">
                        <h5 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Widget Styling</h5>
                        <Button
                            type="button"
                            onClick={() =>
                                setWidgetColors(["#079a95", "#5eb3c1", "#ff9500", "#ff6b35", "#ffc107", "#4caf50", "#9c27b0", "#e91e63"])
                            }
                            variant="float"
                        >
                            Reset
                        </Button>
                    </div>
                    <p className="text-[12px] text-[#5a717a] mb-3 font-roboto">Colors will show in the following order depending the data to be shown</p>
                    <div className="grid grid-cols-4 gap-2">
                        {widgetColors.map((color, index) => (
                            <ColorInput
                                key={index}
                                color={color}
                                index={index}
                                onChange={(newColor) => {
                                    const newColors = [...widgetColors];
                                    newColors[index] = newColor;
                                    setWidgetColors(newColors);
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};